<script setup lang="ts">
  import { computed } from 'vue';

  import type { ValidateTemplateResponse } from '@stuut/api-sdk/models';
  import { Badge } from '@ui/components/badge';
  import { Separator } from '@ui/components/separator';

  const props = defineProps<{
    body: string;
    characterCount?: number;
    validation: ValidateTemplateResponse | null;
    isValidating: boolean;
  }>();

  // Format Jinja variables for display: {{ variable_name }} -> [variable_name]
  const previewBody = computed(() => {
    if (!props.body) return '';
    return props.body.replace(/\{\{\s*(\w+)\s*\}\}/g, '[$1]');
  });

  const displayCharacterCount = computed(() => {
    return props.characterCount ?? props.body?.length ?? 0;
  });

  const isValid = computed(() => {
    return props.validation?.valid ?? true;
  });
</script>

<template>
  <div class="flex flex-col h-full bg-secondary">
    <div class="flex items-center justify-between px-4 py-3 border-b">
      <Badge size="xs" variant="agent" class="w-fit capitalize">Template Preview</Badge>
      <div class="flex items-center gap-2">
        <Badge v-if="isValidating" variant="secondary" size="sm">
          <i class="fa fa-spinner-third animate-spin mr-1" />
          Validating...
        </Badge>
        <Badge v-else-if="isValid" variant="success" size="sm">Valid</Badge>
        <Badge v-else variant="destructive" size="sm">Invalid</Badge>
      </div>
    </div>

    <div class="flex-1 p-4 overflow-y-auto">
      <div
        v-if="body"
        class="bg-background rounded-lg p-4 shadow-sm border whitespace-pre-wrap text-sm"
      >
        {{ previewBody }}
      </div>
      <div v-else class="text-muted-foreground text-sm italic">
        Enter a message body to see the preview
      </div>
    </div>

    <Separator />

    <div class="px-4 py-3 text-xs text-muted-foreground">
      <span>
        <i class="fa fa-text-size mr-1" />
        {{ displayCharacterCount }} characters
      </span>
    </div>

    <template v-if="validation && (validation.errors?.length || validation.warnings?.length)">
      <Separator />
      <div class="px-4 py-3 space-y-2">
        <div
          v-for="(error, index) in validation.errors"
          :key="`error-${index}`"
          class="text-xs text-destructive flex items-start gap-2"
        >
          <i class="fa fa-circle-exclamation mt-0.5" />
          <span>{{ error }}</span>
        </div>
        <div
          v-for="(warning, index) in validation.warnings"
          :key="`warning-${index}`"
          class="text-xs text-amber-600 flex items-start gap-2"
        >
          <i class="fa fa-triangle-exclamation mt-0.5" />
          <span>{{ warning }}</span>
        </div>
      </div>
    </template>
  </div>
</template>
